import { createContext, useContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useSound } from "@/hooks/useSound";
import { trpc } from "@/lib/trpc";

interface WebSocketContextType {
  socket: Socket | null;
  connected: boolean;
  notifications: NotificationEvent[];
  markAsRead: (notificationId: number) => void;
  clearNotifications: () => void;
}

interface NotificationEvent {
  id: number;
  type: string;
  title: string;
  message: string;
  link?: string;
  createdAt: Date;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined
);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState<NotificationEvent[]>([]);

  const { data: settings } = trpc.userSettings.getSettings.useQuery(undefined, {
    enabled: !!user,
  });

  const { playSound } = useSound({
    enabled: settings?.soundEnabled ?? true,
    volume: 0.5,
  });

  useEffect(() => {
    if (user) {
      initializeSocket();
    } else if (socket) {
      socket.disconnect();
      setSocket(null);
      setConnected(false);
      setNotifications([]);
      toast.dismiss();
    }

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [user]);

  const initializeSocket = () => {
    if (socket) {
      return;
    }

    const newSocket = io({
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      withCredentials: true,
    });

    newSocket.on("connect", () => {
      console.log("WebSocket connected");
      setConnected(true);
    });

    newSocket.on("disconnect", () => {
      console.log("WebSocket disconnected");
      setConnected(false);
    });

    newSocket.on("connect_error", error => {
      console.error("WebSocket connection error:", error);
      setConnected(false);
    });

    newSocket.on("notification:new", (notification: NotificationEvent) => {
      console.log("New notification received:", notification);
      setNotifications(prev => [notification, ...prev]);

      if (settings?.soundEnabled) {
        if (
          notification.type === "assignment" ||
          notification.type === "new_assignment"
        ) {
          playSound("new-assignment");
        } else if (notification.type === "status_change") {
          playSound("status-change");
        }
      }

      toast.info(notification.title, {
        description: notification.message,
        action: notification.link
          ? {
              label: "ເບິ່ງລາຍລະອຽດ",
              onClick: () => (window.location.href = notification.link!),
            }
          : undefined,
      });
    });

    setSocket(newSocket);
  };

  const markAsRead = (notificationId: number) => {
    setNotifications(
      prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        ) as any
    );
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  return (
    <WebSocketContext.Provider
      value={{
        socket,
        connected,
        notifications,
        markAsRead,
        clearNotifications,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
}
